import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, LogOut, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DEFAULT_CONTENT, mergeContent } from "@/constants/siteDefaults";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [contentForm, setContentForm] = useState(DEFAULT_CONTENT);
  const [savingContent, setSavingContent] = useState(false);
  const [logoPath, setLogoPath] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("asap_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [enq, gal, cnt, brand] = await Promise.all([
        axios.get(`${API}/enquiries`, auth),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/content`),
        axios.get(`${API}/branding`),
      ]);
      setEnquiries(enq.data);
      setPhotos(gal.data);
      setContentForm(mergeContent(cnt.data));
      setLogoPath(brand.data.logo_path);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("asap_token");
        navigate("/admin/login");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose a photo first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await axios.post(`${API}/gallery`, fd, auth);
      toast.success("Photo added to the gallery");
      setCaption("");
      fileRef.current.value = "";
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, auth);
      toast.success("Photo removed");
      setPhotos((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not remove photo");
    }
  };

  const removeEnquiry = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, auth);
      setEnquiries((e) => e.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await axios.put(`${API}/content`, contentForm, auth);
      toast.success("Website wording updated — it's live now");
    } catch {
      toast.error("Could not save changes");
    } finally {
      setSavingContent(false);
    }
  };

  const uploadLogo = async () => {
    const file = logoRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose a logo file first");
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/branding/logo`, fd, auth);
      setLogoPath(data.logo_path);
      toast.success("Logo updated — it's live now");
      logoRef.current.value = "";
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      await axios.delete(`${API}/branding/logo`, auth);
      setLogoPath(null);
      toast.success("Logo removed — text logo restored");
    } catch {
      toast.error("Could not remove logo");
    }
  };

  const setField = (key) => (e) => setContentForm({ ...contentForm, [key]: e.target.value });
  const setService = (i, key) => (e) => {
    const services = contentForm.services.map((s, idx) => (idx === i ? { ...s, [key]: e.target.value } : s));
    setContentForm({ ...contentForm, services });
  };

  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current_password, new_password: pwForm.new_password }, auth);
      toast.success("Password updated — use it next time you log in");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update password");
    } finally {
      setPwSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("asap_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 px-6 md:px-12 h-16 flex items-center justify-between">
        <p className="font-outfit font-semibold text-slate-900">
          ASAP <span className="text-sky-500">Glass</span> &amp; Aluminium — Admin
        </p>
        <div className="flex items-center gap-3">
          <a href="/" data-testid="admin-view-site-link" className="text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">View site</a>
          <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-button" className="rounded-sm">
            <LogOut size={14} className="mr-1.5" /> Log out
          </Button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => setTab("enquiries")}
            data-testid="admin-tab-enquiries"
            className={`px-5 py-2.5 text-sm font-medium border rounded-sm transition-colors duration-200 ${tab === "enquiries" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >
            Enquiries ({enquiries.length})
          </button>
          <button
            onClick={() => setTab("gallery")}
            data-testid="admin-tab-gallery"
            className={`px-5 py-2.5 text-sm font-medium border rounded-sm transition-colors duration-200 ${tab === "gallery" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >
            Gallery Photos ({photos.length})
          </button>
          <button
            onClick={() => setTab("content")}
            data-testid="admin-tab-content"
            className={`px-5 py-2.5 text-sm font-medium border rounded-sm transition-colors duration-200 ${tab === "content" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >
            Site Content
          </button>
          <button
            onClick={() => setTab("account")}
            data-testid="admin-tab-account"
            className={`px-5 py-2.5 text-sm font-medium border rounded-sm transition-colors duration-200 ${tab === "account" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >
            Account
          </button>
        </div>

        {tab === "enquiries" && (
          <div className="bg-white border border-slate-200" data-testid="admin-enquiries-panel">
            {enquiries.length === 0 ? (
              <p className="p-10 text-sm text-slate-500" data-testid="admin-enquiries-empty">No enquiries yet. New submissions from the website form will appear here.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.map((e, i) => (
                    <TableRow key={e.id} data-testid={`admin-enquiry-row-${i}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</TableCell>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-sm">
                        <a href={`mailto:${e.email}`} className="text-sky-600 hover:underline block">{e.email}</a>
                        {e.phone && <a href={`tel:${e.phone}`} className="text-slate-500 block">{e.phone}</a>}
                      </TableCell>
                      <TableCell className="text-sm">{e.service || "—"}</TableCell>
                      <TableCell className="text-sm max-w-xs"><span className="line-clamp-3">{e.message}</span></TableCell>
                      <TableCell>
                        <button onClick={() => removeEnquiry(e.id)} data-testid={`admin-enquiry-delete-${i}`} className="text-slate-400 hover:text-red-500 transition-colors duration-200" aria-label="Delete enquiry">
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {tab === "gallery" && (
          <div data-testid="admin-gallery-panel">
            <div className="bg-white border border-slate-200 p-6 flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
              <div className="flex-1">
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Photo</label>
                <input ref={fileRef} type="file" accept="image/*" data-testid="admin-gallery-file-input" className="text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />
              </div>
              <div className="flex-1">
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Caption</label>
                <Input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="admin-gallery-caption-input" placeholder="e.g. Shower screen, Campbelltown" className="rounded-sm" />
              </div>
              <Button onClick={upload} disabled={uploading} data-testid="admin-gallery-upload-button" className="bg-slate-900 hover:bg-sky-500 rounded-sm transition-colors duration-300">
                <ImagePlus size={15} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload Photo"}
              </Button>
            </div>
            {photos.length === 0 ? (
              <p className="mt-6 p-10 bg-white border border-slate-200 text-sm text-slate-500" data-testid="admin-gallery-empty">
                No photos uploaded yet — the website is showing sample images. Upload your first job photo above and it will replace them.
              </p>
            ) : (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-1 bg-slate-200 border border-slate-200">
                {photos.map((p, i) => (
                  <div key={p.id} className="relative group bg-white aspect-square" data-testid={`admin-gallery-item-${i}`}>
                    <img src={`${API}/files/${p.storage_path}`} alt={p.caption} className="h-full w-full object-cover" loading="lazy" />
                    <button
                      onClick={() => removePhoto(p.id)}
                      data-testid={`admin-gallery-delete-${i}`}
                      className="absolute top-2 right-2 bg-white/90 p-2 text-slate-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      aria-label="Delete photo"
                    >
                      <Trash2 size={15} />
                    </button>
                    {p.caption && <p className="absolute inset-x-0 bottom-0 bg-slate-900/70 text-white text-xs px-3 py-1.5">{p.caption}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "content" && (
          <div data-testid="admin-content-panel" className="space-y-6">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-outfit text-lg font-medium text-slate-800 mb-1">Business logo</h3>
              <p className="text-sm text-slate-500 mb-5">Upload your logo to replace the text logo in the header and footer. PNG with a transparent background works best.</p>
              <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
                <div className="flex-1">
                  {logoPath && (
                    <div className="mb-4 border border-slate-200 bg-slate-50 p-4 inline-block" data-testid="admin-logo-preview">
                      <img src={`${API}/files/${logoPath}`} alt="Current logo" className="h-12 w-auto" />
                    </div>
                  )}
                  <input ref={logoRef} type="file" accept="image/*" data-testid="admin-logo-file-input" className="block text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />
                </div>
                <Button onClick={uploadLogo} disabled={logoUploading} data-testid="admin-logo-upload-button" className="bg-slate-900 hover:bg-sky-500 rounded-sm transition-colors duration-300">
                  {logoUploading ? "Uploading…" : "Upload Logo"}
                </Button>
                {logoPath && (
                  <Button onClick={removeLogo} variant="outline" data-testid="admin-logo-remove-button" className="rounded-sm">
                    Remove Logo
                  </Button>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-outfit text-lg font-medium text-slate-800 mb-5">Page wording</h3>
              <div className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Top line (small text above the main heading)</label>
                  <Input value={contentForm.hero_eyebrow} onChange={setField("hero_eyebrow")} data-testid="content-hero-eyebrow-input" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Main heading</label>
                  <Textarea rows={2} value={contentForm.hero_heading} onChange={setField("hero_heading")} data-testid="content-hero-heading-input" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Intro sentence under the heading</label>
                  <Textarea rows={2} value={contentForm.hero_subtitle} onChange={setField("hero_subtitle")} data-testid="content-hero-subtitle-input" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">About section heading</label>
                  <Input value={contentForm.about_heading} onChange={setField("about_heading")} data-testid="content-about-heading-input" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">About section text</label>
                  <Textarea rows={4} value={contentForm.about_text} onChange={setField("about_text")} data-testid="content-about-text-input" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Contact section heading</label>
                  <Input value={contentForm.contact_heading} onChange={setField("contact_heading")} data-testid="content-contact-heading-input" className="rounded-sm" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-outfit text-lg font-medium text-slate-800 mb-1">Services</h3>
              <p className="text-sm text-slate-500 mb-5">Edit the name and description of each service shown on the website.</p>
              <div className="space-y-5">
                {contentForm.services.map((s, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3 border border-slate-200 p-4" data-testid={`content-service-${i}`}>
                    <Input value={s.title} onChange={setService(i, "title")} data-testid={`content-service-title-${i}`} placeholder="Service name" className="rounded-sm font-medium" />
                    <Textarea rows={2} value={s.desc} onChange={setService(i, "desc")} data-testid={`content-service-desc-${i}`} placeholder="Short description" className="rounded-sm md:col-span-2" />
                  </div>
                ))}
              </div>
            </div>

            <div className="sticky bottom-4">
              <Button onClick={saveContent} disabled={savingContent} data-testid="content-save-button" className="w-full sm:w-auto bg-slate-900 hover:bg-sky-500 text-white rounded-sm px-10 py-6 text-base font-medium shadow-lg transition-colors duration-300">
                {savingContent ? "Saving…" : "Save All Changes"}
              </Button>
            </div>
          </div>
        )}

        {tab === "account" && (
          <div data-testid="admin-account-panel" className="max-w-md">
            <form onSubmit={changePassword} className="bg-white border border-slate-200 p-6" data-testid="change-password-form">
              <h3 className="font-outfit text-lg font-medium text-slate-800 mb-1">Change password</h3>
              <p className="text-sm text-slate-500 mb-6">Choose a strong password only you know. You'll use it next time you log in.</p>
              <div className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Current password</label>
                  <Input type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="current-password-input" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">New password (min 8 characters)</label>
                  <Input type="password" required minLength={8} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="new-password-input" className="rounded-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Repeat new password</label>
                  <Input type="password" required minLength={8} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="confirm-password-input" className="rounded-sm" />
                </div>
              </div>
              <Button type="submit" disabled={pwSaving} data-testid="change-password-submit-button" className="mt-7 bg-slate-900 hover:bg-sky-500 rounded-sm px-8 transition-colors duration-300">
                {pwSaving ? "Updating…" : "Update Password"}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
